import MSEAudioWrapper from 'mse-audio-wrapper';
import CodecParser from 'codec-parser';

const MAX_AUDIO_BYTES = 12 * 1024 * 1024;

function mediaOperation(target, event, signal, source, action) {
  return new Promise((resolve, reject) => {
    const cleanup = () => {
      target.removeEventListener(event, ready);
      target.removeEventListener('error', failed);
      source.removeEventListener('sourceclose', failed);
      signal.removeEventListener('abort', cancelled);
    };
    const ready = () => { cleanup(); resolve(); };
    const failed = () => { cleanup(); reject(new Error('audio_unavailable')); };
    const cancelled = () => { cleanup(); reject(signal.reason); };
    target.addEventListener(event, ready, { once: true });
    target.addEventListener('error', failed, { once: true });
    source.addEventListener('sourceclose', failed, { once: true });
    signal.addEventListener('abort', cancelled, { once: true });
    if (signal.aborted) { cancelled(); return; }
    try { action?.(); } catch (error) { cleanup(); reject(error); }
  });
}

// Fetch synthesis once and retain the recording for local seeking and replay.
// fMP4 wrapping enables browsers that cannot append raw MP3 to a SourceBuffer.
export function createStreamingAudio(audio, signal, scope = window) {
  const types = ['audio/mpeg', 'audio/mp4;codecs="mp3"'];
  const sources = [scope.MediaSource, scope.ManagedMediaSource].filter(Boolean);
  let mime, Source;
  for (const type of types) {
    Source = sources.find(candidate => candidate.isTypeSupported?.(type));
    if (Source) { mime = type; break; }
  }
  const source = Source ? new Source() : null;
  const wrapper = source && mime !== 'audio/mpeg'
    ? new MSEAudioWrapper('audio/mpeg', { codec: 'mpeg', minFramesPerSegment: 1, minBytesPerSegment: 1 }) : null;
  const parser = wrapper ? new CodecParser('audio/mpeg', { enableFrameCRC32: false }) : null;
  let url = source ? scope.URL.createObjectURL(source) : '';
  let reader;
  let disposed = false;
  const abort = () => { reader?.cancel().catch(() => {}); };
  signal.addEventListener('abort', abort, { once: true });
  if (url) {
    // ManagedMediaSource on iPhone needs remote playback disabled.
    audio.disableRemotePlayback = true;
    audio.src = url;
    audio.load();
  }

  return {
    get url() { return url; },
    async load(response) {
      if (!response.body || disposed) throw new Error('audio_unavailable');
      signal.throwIfAborted();
      reader = response.body.getReader();
      const chunks = [];
      let bytes = 0;
      let buffer;
      try {
        if (source) {
          if (source.readyState !== 'open') await mediaOperation(source, 'sourceopen', signal, source);
          signal.throwIfAborted();
          buffer = source.addSourceBuffer(mime);
        }
        const append = value => mediaOperation(buffer, 'updateend', signal, source, () => buffer.appendBuffer(value));
        let reading = true;
        while (reading) {
          const { done, value } = await reader.read();
          signal.throwIfAborted();
          if (disposed) throw new Error('audio_disposed');
          if (done) { reading = false; continue; }
          bytes += value.byteLength;
          if (bytes > MAX_AUDIO_BYTES) throw new Error('audio_too_large');
          if (wrapper) {
            for (const segment of wrapper.iterator([...parser.parseChunk(value)])) await append(segment);
          } else if (buffer) await append(value);
          else chunks.push(value);
        }
        if (!bytes) throw new Error('audio_unavailable');
        if (source) {
          if (wrapper) for (const segment of wrapper.iterator([...parser.flush()])) await append(segment);
          source.endOfStream();
        } else {
          // Retain compatibility when neither streaming container is supported.
          url = scope.URL.createObjectURL(new Blob(chunks, { type: 'audio/mpeg' }));
          audio.src = url;
          audio.load();
        }
        return url;
      } catch (error) {
        await reader.cancel().catch(() => {});
        throw error;
      } finally {
        reader.releaseLock();
        reader = null;
        signal.removeEventListener('abort', abort);
      }
    },
    dispose() {
      if (disposed) return;
      disposed = true;
      abort();
      signal.removeEventListener('abort', abort);
      audio.pause();
      audio.removeAttribute('src');
      audio.load();
      if (url) scope.URL.revokeObjectURL(url);
      url = '';
    },
  };
}
